import { ContentDto } from '@app/modules/content-ai/content-ai.types';
import { useContentAiUpdateUserArticleContent } from '@mi-front/api-v2';
import { Box } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';
import { useWizard } from 'react-use-wizard';
import { useContent } from '../../../../hooks/useContent';
import { ContentBadge, ContentBody, ContentTitle, PrimaryButton } from '../../components';

export function AiArticlesSelector({
  content,
  onRegenerate,
}: {
  content: ContentDto;
  onRegenerate?: () => void;
}) {
  const queryClient = useQueryClient();
  const { nextStep } = useWizard();
  const mutation = useContentAiUpdateUserArticleContent();
  const query = useContent(content.id);

  if (!content.aiArticles.data) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
      }}
    >
      {/* Articles */}
      {[content.aiArticles.data.article1!, content.aiArticles.data.article2!].map(
        (article, index) => (
          <Box
            key={`article-${index}`}
            sx={{
              backgroundColor: 'background.paper',
              borderRadius: '8px',
              padding: '32px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              border: '1px solid #D0D4D9',
            }}
          >
            {/* Header with article number */}
            <ContentBadge sx={{ alignSelf: 'flex-start', textTransform: 'uppercase' }}>
              <FormattedMessage
                id="contentAi.articleNumber"
                defaultMessage="ARTÍCULO {number}"
                values={{ number: index + 1 }}
              />
            </ContentBadge>

            {/* Title */}
            <ContentTitle component="h2" sx={{ margin: 0 }}>
              {article.title}
            </ContentTitle>

            {/* Body */}
            <ContentBody component="p" sx={{ margin: 0 }}>
              {article.body}
            </ContentBody>

            {/* Action button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <PrimaryButton
                onClick={() => {
                  mutation.mutate(
                    {
                      id: content.id,
                      data: {
                        title: article.title!,
                        body: article.body!,
                      },
                    },
                    {
                      onSuccess: async () => {
                        await queryClient.refetchQueries({
                          queryKey: query.queryKey,
                        });

                        // Pequeño delay para asegurar que React renderizó
                        setTimeout(() => {
                          nextStep();
                        }, 150);
                      },
                    },
                  );
                }}
                loading={mutation.isPending}
                sx={{ '&:hover': { backgroundColor: '#9C3FCC' } }}
              >
                <FormattedMessage
                  id="contentAi.useThisArticle"
                  defaultMessage="Usar este artículo"
                />
              </PrimaryButton>
            </Box>
          </Box>
        ),
      )}

      {/* Regenerate button */}
      <Box sx={{ mt: 2 }}>
        <PrimaryButton onClick={onRegenerate}>
          <FormattedMessage
            id="contentAi.regenerateArticles"
            defaultMessage="Volver a generar artículos"
          />
        </PrimaryButton>
      </Box>
    </Box>
  );
}
